'use client';

import { Fragment, type MouseEvent, useEffect, useMemo, useState } from 'react';
import { ArrowDown02Icon } from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import {
  BadgeSeverity,
  isBadgeSeverityType,
} from '../../../components/styled/badge-severity';
import {
  BadgeStatus,
  FINDING_STATUS_BADGE_STATUS_MAP,
  isMappedFindingStatus,
} from '../../../components/styled/badge-status';
import {
  MetaDefault,
  BodyDefault,
  BodyStrong,
} from '../../../components/typography';
import { formatFindingStatusForDisplay } from '../../../utils/category-utils';
import { EntityIcon } from '../../icons/EntityIcon';
import { ArchivedLifecycleBadge } from '../../../components/archived-lifecycle-badge';

export type FindingChainNodeSource = {
  id: string;
  title: string | null;
  status: string | null;
  severity?: string | null;
  discoveryDate?: string | Date | null;
  isArchived?: boolean;
};
export type FindingChainFlowProps = {
  findings: FindingChainNodeSource[];
  orderedIds: string[];
  onFindingClick?: (id: string) => void;
  onFindingContextMenu?: (event: MouseEvent<HTMLElement>, id: string) => void;
};
type ChainStepData = {
  id: string;
  title: string;
  status: string | null;
  severity: string | null;
  discoveryDate: string | Date | null;
  isArchived: boolean;
  isMissing: boolean;
  stepNumber: number;
};
type ChainStepRowProps = {
  step: ChainStepData;
  onNavigate?: (id: string) => void;
  onContextMenu?: (event: MouseEvent<HTMLElement>, id: string) => void;
};
const ChainStepRow = ({
  step,
  onNavigate,
  onContextMenu,
}: ChainStepRowProps) => {
  const dateFormatter = useDateFormatter();
  const interactive = !step.isMissing && Boolean(onNavigate);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const severity = isBadgeSeverityType(step.severity) ? step.severity : null;
  const findingStatus = isMappedFindingStatus(step.status) ? step.status : null;
  const statusBadge = findingStatus
    ? FINDING_STATUS_BADGE_STATUS_MAP[findingStatus]
    : null;
  const formattedDiscoveryDate = (() => {
    if (!step.discoveryDate) {
      return null;
    }

    const date =
      step.discoveryDate instanceof Date
        ? step.discoveryDate
        : new Date(step.discoveryDate);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return dateFormatter.formatDateOnly(date);
  })();

  useEffect(() => {
    if (!isPressed) {
      return;
    }

    const clearPressedState = () => {
      setIsPressed(false);
    };

    window.addEventListener('mouseup', clearPressedState);
    window.addEventListener('blur', clearPressedState);

    return () => {
      window.removeEventListener('mouseup', clearPressedState);
      window.removeEventListener('blur', clearPressedState);
    };
  }, [isPressed]);
  const cardBorderColor = isPressed
    ? 'border.neutral.emphasized'
    : isHovered
      ? 'border.neutral.focus'
      : 'border.neutral.subtle';
  const cardBgColor = step.isMissing
    ? 'bg.muted'
    : isPressed || isHovered
      ? 'bg.neutral.subtleSoft'
      : 'bg.neutral.light';

  return (
    <Box
      w="full"
      px={2}
      py={3}
      bg={cardBgColor}
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={cardBorderColor}
      cursor={interactive ? 'pointer' : 'default'}
      opacity={step.isMissing ? 0.6 : 1}
      onMouseEnter={() => {
        if (interactive) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (interactive) {
          setIsHovered(false);
        }
      }}
      onMouseDown={() => {
        if (interactive) {
          setIsPressed(true);
        }
      }}
      onMouseUp={() => {
        if (interactive) {
          setIsPressed(false);
        }
      }}
      onClick={() => {
        if (!step.isMissing) {
          onNavigate?.(step.id);
        }
      }}
      onContextMenu={(event) => {
        if (!step.isMissing) {
          onContextMenu?.(event, step.id);
        }
      }}
    >
      <HStack gap={3} alignItems="flex-start" w="full">
        <Box
          flexShrink={0}
          w={6}
          h={6}
          borderRadius="full"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.default"
          color="fg.default"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mt={0.5}
        >
          <MetaDefault color="fg.default">
            {String(step.stepNumber)}
          </MetaDefault>
        </Box>
        <VStack alignItems="stretch" gap={2} flex={1} minW={0}>
          <HStack gap={2} alignItems="center" justifyContent="space-between">
            <HStack gap={2} alignItems="center" minW={0} flex={1}>
              <EntityIcon
                type="finding"
                appearance="highlighted"
                density="compact"
              />
              <BodyStrong lineClamp={2}>{step.title}</BodyStrong>
              {step.isArchived ? (
                <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
              ) : null}
            </HStack>
            {severity && (
              <BadgeSeverity severity={severity} size="sm">
                {severity}
              </BadgeSeverity>
            )}
          </HStack>
          <HStack gap={2} alignItems="center" justifyContent="space-between">
            {statusBadge ? (
              <BadgeStatus status={statusBadge} size="sm">
                {formatFindingStatusForDisplay(findingStatus ?? '')}
              </BadgeStatus>
            ) : (
              <Box />
            )}
            {formattedDiscoveryDate && (
              <MetaDefault color="fg.muted">
                {formattedDiscoveryDate}
              </MetaDefault>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
};

const ChainConnector = () => (
  <Box
    color="fg.muted"
    display="flex"
    alignItems="center"
    justifyContent="center"
    py={1}
    w="full"
  >
    <ArrowDown02Icon size={16} aria-hidden />
  </Box>
);

export const FindingChainFlow = ({
  findings,
  orderedIds,
  onFindingClick,
  onFindingContextMenu,
}: FindingChainFlowProps) => {
  const t = useTranslations('designSystem.findings.linkedFindings');
  const missingLabel = t('missingFinding');
  const findingsById = useMemo(
    () => new Map(findings.map((finding) => [finding.id, finding])),
    [findings],
  );
  const chainSteps = useMemo((): ChainStepData[] => {
    return orderedIds.map((id, index) => {
      const source = findingsById.get(id);

      return source
        ? {
            id: source.id,
            title: source.title || missingLabel,
            status: source.status,
            severity: source.severity ?? null,
            discoveryDate: source.discoveryDate ?? null,
            isArchived: source.isArchived === true,
            isMissing: false,
            stepNumber: index + 1,
          }
        : {
            id,
            title: missingLabel,
            status: null,
            severity: null,
            discoveryDate: null,
            isArchived: false,
            isMissing: true,
            stepNumber: index + 1,
          };
    });
  }, [orderedIds, findingsById, missingLabel]);

  if (orderedIds.length === 0) {
    return (
      <Box
        w="full"
        py={4}
        textAlign="center"
        color="fg.muted"
        bg="bg.subtle"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
      >
        <BodyDefault color="fg.muted">{t('emptyChain')}</BodyDefault>
      </Box>
    );
  }

  return (
    <VStack gap={0} alignItems="stretch" w="full">
      {chainSteps.map((step, index) => (
        <Fragment key={step.id}>
          {index > 0 && <ChainConnector />}
          <ChainStepRow
            step={step}
            onNavigate={onFindingClick}
            onContextMenu={onFindingContextMenu}
          />
        </Fragment>
      ))}
    </VStack>
  );
};
